import {
    action,
    computed,
    makeObservable,
    observable,
    runInAction,
} from "mobx";
import { ColumnsFilter } from "../../common/services/types";
import { TasksData } from "../types";
import TasksRootStore from "./TasksRootStore";
import { ALL_PROJECTS } from "../screens/TasksPage";
import { ColumnKeys, FilterOperator } from "../components/TaskList";
import { ExtraDateFilter } from "src/modules/administration/types/DashboardWidgetMeta";
import { METADATA_CHANGES_FILTER_KEY } from "../components/FiltersControls";

const WIDGET_SPECFIC_FIELDS = [
    "alphaId",
    "followers",
    "id",
    "isManuallyCreated",
    "name",
    "updateDate",
    "updatedBy",
];
export default class TasksFiltersVisualStore {
    columnsFilterDict: { [key: string]: ColumnsFilter } = {};

    constructor(private tasksRootStore: TasksRootStore) {
        makeObservable<TasksFiltersVisualStore>(this, {
            columnsFilterDict: observable,
            setCurrentFilterValue: action,
            resetColumnFilter: action,
            changeFilterOperator: action,
            taskTypesForCurrentProject: computed,
            handleAllFiltersReset: action.bound,
            isWidgetView: computed,
        });
    }

    get taskTypesForCurrentProject() {
        return this.tasksRootStore.taskTypesForCurrentProject;
    }

    get taskStatusesForCurrentProject() {
        return this.tasksRootStore.taskStatusesForCurrentProject;
    }

    get allUsersFullNameResolver() {
        return this.tasksRootStore.activeUsersFullNameResolver;
    }

    get isWidgetView() {
        return this.tasksRootStore.isWidgetView;
    }

    setColumnsFilter(tasks: TasksData) {
        if (tasks.userColumnsFilter) {
            for (const colFilter of tasks.userColumnsFilter.filters) {
                this.columnsFilterDict[colFilter.entityReference] = {
                    entityReference: colFilter.entityReference,
                    value: colFilter.value,
                    fieldType: colFilter.fieldType,
                    operator: colFilter.operator,
                    extraFilters: colFilter.extraFilters,
                    isWidgetFilter: false,
                };
            }
        }

        const widgetFilters = tasks.widgetFilters.filter(
            (w) => !WIDGET_SPECFIC_FIELDS.includes(w.entityReference),
        );
        for (const colFilter of widgetFilters) {
            this.columnsFilterDict[colFilter.entityReference] = {
                entityReference: colFilter.entityReference,
                value: colFilter.value,
                fieldType: colFilter.fieldType,
                operator: colFilter.operator,
                extraFilters: colFilter.extraFilters,
                isWidgetFilter: true,
            };
        }
    }

    async setDateFilterValue(
        columnName: string,
        extraDateFilter: ExtraDateFilter | null,
        isMetaData: boolean,
    ) {
        this.columnsFilterDict[columnName] = {
            entityReference: columnName,
            operator: FilterOperator.Equal,
            value: [],
            extraFilters: extraDateFilter
                ? { extraDateFilter }
                : { extraDateFilter: null },
            fieldType: isMetaData ? "Metadata" : "General",
            isWidgetFilter: false,
        };
        this.tasksRootStore.setTasksIsLoading(true);
        const projectId =
            this.tasksRootStore.selectedProject === ALL_PROJECTS
                ? null
                : this.tasksRootStore.selectedProject;
        await this.tasksRootStore.updateColumnFilters(
            projectId,
            this.prepareRequestFilters(),
        );
        this.tasksRootStore.setTasksIsLoading(false);
    }

    async setCurrentFilterValue(
        columnName: string,
        value: unknown,
        isMetaData: boolean,
    ) {
        if (this.columnsFilterDict[columnName]) {
            if (
                columnName === ColumnKeys.SubTasks ||
                columnName === ColumnKeys.Attachments ||
                columnName === ColumnKeys.DoneTasks ||
                columnName === METADATA_CHANGES_FILTER_KEY
            ) {
                if (this.columnsFilterDict[columnName].value[0] !== value) {
                    this.columnsFilterDict[columnName].value = [value];
                } else {
                    return;
                }
            } else if (
                columnName === ColumnKeys.DateCreated ||
                columnName === ColumnKeys.DueDate
            ) {
                this.columnsFilterDict[columnName].value = value as string[];
            } else {
                if (!isMetaData) {
                    let filterValue = this.columnsFilterDict[columnName].value;
                    if (filterValue.some((v) => v === value)) {
                        filterValue = filterValue.filter((v) => v !== value);
                    } else {
                        filterValue = [...filterValue, value];
                    }
                    runInAction(() => {
                        if (filterValue.length === 0) {
                            this.removeFilter(columnName);
                        } else {
                            this.columnsFilterDict[columnName].value =
                                filterValue;
                        }
                    });
                } else {
                    if (!(value as string).length) {
                        this.removeFilter(columnName);
                    } else {
                        this.columnsFilterDict[columnName].value =
                            value as string[];
                    }
                }
            }
        } else {
            this.columnsFilterDict[columnName] = {
                entityReference: columnName,
                operator: FilterOperator.Equal,
                value: Array.isArray(value) ? (value as string[]) : [value],
                extraFilters: {},
                fieldType: isMetaData ? "Metadata" : "General",
                isWidgetFilter: false,
            };
        }
        this.tasksRootStore.setTasksIsLoading(true);
        const projectId =
            this.tasksRootStore.selectedProject === ALL_PROJECTS
                ? null
                : this.tasksRootStore.selectedProject;
        await this.tasksRootStore.updateColumnFilters(
            projectId,
            this.prepareRequestFilters(),
        );
        this.tasksRootStore.setTasksIsLoading(false);
    }

    addCustomDateFilter(columnName: string, isMetaData: boolean) {
        this.columnsFilterDict[columnName] = {
            entityReference: columnName,
            operator: FilterOperator.Equal,
            value: [],
            extraFilters: { extraDateFilter: null },
            fieldType: isMetaData ? "Metadata" : "General",
            isWidgetFilter: false,
            customFilter: true,
        };
    }

    async changeFilterOperator(columnName: string, operator: string) {
        this.columnsFilterDict[columnName].operator = operator;
        this.tasksRootStore.setTasksIsLoading(true);
        const projectId =
            this.tasksRootStore.selectedProject === ALL_PROJECTS
                ? null
                : this.tasksRootStore.selectedProject;
        await this.tasksRootStore.updateColumnFilters(
            projectId,
            this.prepareRequestFilters(),
        );
        this.tasksRootStore.setTasksIsLoading(false);
    }

    unsetAllColumnsFilters() {
        runInAction(() => (this.columnsFilterDict = {}));
    }

    async resetColumnFilter(columnName: string) {
        this.tasksRootStore.setTasksIsLoading(true);
        const projectId =
            this.tasksRootStore.selectedProject === ALL_PROJECTS
                ? null
                : this.tasksRootStore.selectedProject;
        this.removeFilter(columnName);
        await this.tasksRootStore.updateColumnFilters(
            projectId,
            this.prepareRequestFilters(),
        );
        this.tasksRootStore.setTasksIsLoading(false);
    }

    async handleAllFiltersReset() {
        this.tasksRootStore.setTasksIsLoading(true);
        await this.tasksRootStore.resetAllFilters();
        this.tasksRootStore.setTasksIsLoading(false);
    }

    removeFilter(columnName: string) {
        delete this.columnsFilterDict[columnName];
    }

    prepareRequestFilters() {
        return (
            Object.entries(this.columnsFilterDict)
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                .filter(([_, x]) => !x.isWidgetFilter)
                .map(([key, f]) => {
                    if (f.fieldType === "Metadata") {
                        f.entityReference = key;
                    }
                    return f;
                })
        );
    }
}
